/**
 * Per-source identity for calendar events (PRD §5.4/§7.2).
 *
 * One badge per event source, built on Chip so tones come from role tokens —
 * color is never the only differentiator (each badge carries a text label).
 * Public events additionally render their licensed-source credit via
 * <EventAttribution> (PRD §5.6.6).
 */
import React from 'react';
import Chip from '../Chip';
import type { CalendarEventSource } from '../../types/calendar';

const SOURCE_CONFIG: Record<CalendarEventSource, { label: string; tone: 'neutral' | 'info' | 'caution' | 'brand' }> = {
  legislative: { label: 'Legislative', tone: 'neutral' },
  election: { label: 'Election', tone: 'caution' },
  public: { label: 'Public', tone: 'info' },
  workspace: { label: 'Workspace', tone: 'brand' }
};

export const SOURCE_LABELS: Record<CalendarEventSource, string> = {
  legislative: SOURCE_CONFIG.legislative.label,
  election: SOURCE_CONFIG.election.label,
  public: SOURCE_CONFIG.public.label,
  workspace: SOURCE_CONFIG.workspace.label
};

interface EventSourceBadgeProps {
  source: CalendarEventSource;
  /** Workspace name refines the generic "Workspace" label when known */
  workspaceName?: string | null;
}

const EventSourceBadge: React.FC<EventSourceBadgeProps> = ({ source, workspaceName }) => {
  const config = SOURCE_CONFIG[source];
  if (!config) return null;
  const label = source === 'workspace' && workspaceName ? workspaceName : config.label;
  return <Chip label={label} tone={config.tone} size="sm" />;
};

/** Licensed-source credit line, e.g. "Provided by Colorado Politics" (§5.6.6). */
export const EventAttribution: React.FC<{ attribution?: { text: string; url: string | null } | null }> = ({
  attribution
}) => {
  if (!attribution?.text) return null;
  const className = 'text-[11px] italic text-slate-400 dark:text-text-dark-tertiary';
  return attribution.url ? (
    <a href={attribution.url} target="_blank" rel="noopener noreferrer" className={`${className} hover:underline`}>
      {attribution.text}
    </a>
  ) : (
    <span className={className}>{attribution.text}</span>
  );
};

export default EventSourceBadge;
